import { getAllClients, getDropdownData } from '../components/Queries';

/**
 * Client Logo Service - Common utility to fetch client logos across the application
 * 
 * This service provides functionality to:
 * 1. Get client logo by program code
 * 2. Cache results for better performance
 * 3. Handle multiple program codes
 * 4. Provide fallback mechanisms
 */

// Cache for storing client data and dropdown data
let clientsCache = null;
let dropdownCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Clear the cache (useful for testing or when data is updated)
 */
export const clearClientLogoCache = () => {
    clientsCache = null;
    dropdownCache = null;
    cacheTimestamp = null;
};

/**
 * Check if cache is valid
 */
const isCacheValid = () => {
    return cacheTimestamp && (Date.now() - cacheTimestamp) < CACHE_DURATION;
};

/**
 * Load and cache client and dropdown data
 */
const loadCachedData = async () => {
    if (isCacheValid() && clientsCache && dropdownCache) {
        return { clients: clientsCache, dropdown: dropdownCache };
    }

    try {
        console.log('Loading client and dropdown data...');
        const [clientsResponse, dropdownResponse] = await Promise.all([
            getAllClients(),
            getDropdownData()
        ]);

        clientsCache = clientsResponse || [];
        dropdownCache = dropdownResponse || [];
        cacheTimestamp = Date.now();

        return { clients: clientsCache, dropdown: dropdownCache };
    } catch (error) {
        console.error('Error loading client/dropdown data:', error);
        return { clients: [], dropdown: [] };
    }
};

/**
 * Get client logo by program code
 * 
 * @param {string} programCode - The program code to find client for
 * @returns {Promise<string|null>} - Client logo URL or null if not found
 */
export const getClientLogoByProgramCode = async (programCode) => {
    if (!programCode) return null;
    
    try {
        const { clients, dropdown } = await loadCachedData();

        // Find the dropdown entry with matching program code
        const programEntry = dropdown.find(item => 
            item.type === 'program_code' && 
            item.name === programCode
        );

        if (!programEntry) {
            console.warn(`[ClientLogoService] No program entry found for program code: ${programCode}`);
            console.log('[ClientLogoService] Available program codes:', dropdown.filter(item => item.type === 'program_code').map(item => item.name));
            return null;
        }

        // Check if the program entry has an image field directly (for backward compatibility)
        if (programEntry.image) {
            return programEntry.image;
        }

        // Fall back to client lookup if no direct image
        if (!programEntry.client_code) {
            console.warn(`[ClientLogoService] No client_code found for program code: ${programCode}`);
            return null;
        }

        // Find the client with matching client_code
        const client = clients.find(client => 
            client.client_code === programEntry.client_code
        );

        if (!client) {
            console.warn(`[ClientLogoService] No client found for client_code: ${programEntry.client_code}`);
            console.log('[ClientLogoService] Available client codes:', clients.map(client => client.client_code));
            return null;
        }

        return client.client_logo || null;
    } catch (error) {
        console.error(`Error getting client logo for program code ${programCode}:`, error);
        return null;
    }
};

/**
 * Get client logos for multiple program codes
 * 
 * @param {string[]} programCodes - Array of program codes
 * @returns {Promise<Object>} - Object mapping program codes to logo URLs
 */
export const getClientLogosByProgramCodes = async (programCodes) => {
    if (!Array.isArray(programCodes) || programCodes.length === 0) {
        return {};
    }

    try {
        const logoPromises = programCodes.map(async (programCode) => {
            const logo = await getClientLogoByProgramCode(programCode);
            return { programCode, logo };
        });

        const results = await Promise.all(logoPromises);
        
        // Convert to object for easy lookup
        const logoMap = {};
        results.forEach(({ programCode, logo }) => {
            logoMap[programCode] = logo;
        });

        return logoMap;
    } catch (error) {
        console.error('Error getting multiple client logos:', error);
        return {};
    }
};

/**
 * Get client information by program code (includes logo, name, description, etc.)
 * 
 * @param {string} programCode - The program code to find client for
 * @returns {Promise<Object|null>} - Client object or null if not found
 */
export const getClientByProgramCode = async (programCode) => {
    if (!programCode) return null;

    try {
        const { clients, dropdown } = await loadCachedData();

        // Find the dropdown entry with matching program code
        const programEntry = dropdown.find(item => 
            item.type === 'program_code' && 
            item.name === programCode
        );

        if (!programEntry || !programEntry.client_code) {
            return null;
        }
        // Find the client with matching client_code
        const client = clients.find(client => 
            client.client_code === programEntry.client_code
        );

        return client || null;
    } catch (error) {
        console.error(`Error getting client for program code ${programCode}:`, error);
        return null;
    }
};

/**
 * Get the primary client logo for a user (first program code's client logo)
 * 
 * @param {string|string[]} userProgramCodes - Single program code or array of program codes
 * @returns {Promise<string|null>} - Primary client logo URL
 */
export const getPrimaryClientLogo = async (userProgramCodes) => {
    if (!userProgramCodes) return null;
    // Handle both string and array inputs
    const programCodes = Array.isArray(userProgramCodes) 
        ? userProgramCodes 
        : userProgramCodes.split(',').map(code => String(code || '').trim()).filter(Boolean);

    if (programCodes.length === 0) return null;
    // Get the logo for the first program code (primary)
    return await getClientLogoByProgramCode(programCodes[0]);
};

/**
 * Check if all program codes belong to the same client
 * 
 * @param {string|string[]} userProgramCodes - Single program code or array of program codes
 * @returns {Promise<{sameClient: boolean, clientCode: string|null, programCodes: string[]}>}
 */
export const checkProgramCodesSameClient = async (userProgramCodes) => {
    if (!userProgramCodes) return { sameClient: false, clientCode: null, programCodes: [] };

    try {
        // Handle both string and array inputs
        const programCodes = Array.isArray(userProgramCodes) 
            ? userProgramCodes 
            : userProgramCodes.split(',').map(code => String(code || '').trim()).filter(Boolean);

        if (programCodes.length === 0) return { sameClient: false, clientCode: null, programCodes: [] };
        if (programCodes.length === 1) return { sameClient: true, clientCode: null, programCodes };


        const { dropdown } = await loadCachedData();
        
        // Get client codes for all program codes
        const clientCodes = [];
        for (const programCode of programCodes) {
            const programEntry = dropdown.find(item => 
                item.type === 'program_code' && 
                item.name === programCode
            );
            
            if (programEntry?.client_code) {
                clientCodes.push(programEntry.client_code);
            } else {
                console.warn(`[ClientLogoService] No client_code found for program code: ${programCode}`);
            }
        }

        // Check if all client codes are the same
        const uniqueClientCodes = [...new Set(clientCodes)];
        const sameClient = uniqueClientCodes.length === 1 && clientCodes.length === programCodes.length;
        

        return {
            sameClient,
            clientCode: sameClient ? uniqueClientCodes[0] : null,
            programCodes: sameClient ? programCodes : [programCodes[0]] // Return only first if different clients
        };

    } catch (error) {
        console.error('Error checking program codes same client:', error);
        return { sameClient: false, clientCode: null, programCodes: [] };
    }
};

/**
 * Get program codes that should be used for filtering assessments
 * If all user's program codes belong to same client, return all
 * Otherwise, return only the primary (first) program code
 * 
 * @param {string|string[]} userProgramCodes - User's program codes
 * @returns {Promise<string[]>} - Array of program codes to use for filtering
 */
export const getFilterProgramCodes = async (userProgramCodes) => {
    const result = await checkProgramCodesSameClient(userProgramCodes);
    return result.programCodes;
};

/**
 * Preload client logos for better performance
 * Call this on app startup or when entering sections that use client logos
 */
export const preloadClientLogos = async () => {
    try {
        await loadCachedData();
    } catch (error) {
        console.error('Error preloading client logos:', error);
    }
};

const clientLogoService = {
    getClientLogoByProgramCode,
    getClientLogosByProgramCodes,
    getClientByProgramCode,
    getPrimaryClientLogo,
    checkProgramCodesSameClient,
    getFilterProgramCodes,
    preloadClientLogos,
    clearClientLogoCache
};

export default clientLogoService;